import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", labelKey: "instagram", fallback: "Instagram" },
  { value: "tiktok", labelKey: "tiktok", fallback: "TikTok" },
  { value: "youtube", labelKey: "youtube", fallback: "YouTube" },
  { value: "x", labelKey: "xTwitter", fallback: "X / Twitter" },
  { value: "facebook", labelKey: "facebook", fallback: "Facebook" },
  { value: "linkedin", labelKey: "linkedin", fallback: "LinkedIn" },
  { value: "general", labelKey: "general", fallback: "General" },
];

const outputTypeOptions = [
  { value: "voice_profile", labelKey: "voiceProfile", fallback: "Voice Profile" },
  { value: "rewrite_sample", labelKey: "rewriteSample", fallback: "Rewrite Sample" },
  { value: "brand_guidelines", labelKey: "brandGuidelines", fallback: "Brand Guidelines" },
  { value: "dos_and_donts", labelKey: "dosAndDonts", fallback: "Do's and Don'ts" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.text ||
            item.content ||
            item.label ||
            item.title ||
            item.name ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const formatResultToText = (result, t) => {
  if (!result) return "";
  if (typeof result === "string") return result;

  const sections = [];

  const voiceProfile =
    result.voice_profile ||
    result.brand_voice ||
    result.profile ||
    result.output ||
    result.result;

  if (voiceProfile) {
    sections.push(`${t("studioTools.brandVoiceTrainerTool.sections.brandVoiceProfile")}:\n${voiceProfile}`);
  }

  const summary = result.summary || result.overview || result.analysis || result.explanation;
  if (summary) {
    sections.push(`${t("studioTools.brandVoiceTrainerTool.sections.summary")}:\n${summary}`);
  }

  const pillars = normalizeArray(
    result.voice_pillars || result.pillars || result.core_traits || result.traits
  );
  if (pillars.length) {
    sections.push(
      `${t("studioTools.brandVoiceTrainerTool.sections.voicePillars")}:\n${pillars
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const dos = normalizeArray(result.dos || result.do_list || result.best_practices);
  if (dos.length) {
    sections.push(
      `${t("studioTools.brandVoiceTrainerTool.sections.dos")}:\n${dos
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const donts = normalizeArray(result.donts || result.do_not || result.avoid);
  if (donts.length) {
    sections.push(
      `${t("studioTools.brandVoiceTrainerTool.sections.donts")}:\n${donts
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const sampleRewrite = result.sample_rewrite || result.rewrite_example || result.example_output;
  if (sampleRewrite) {
    sections.push(`${t("studioTools.brandVoiceTrainerTool.sections.sampleRewrite")}:\n${sampleRewrite}`);
  }

  const notes = normalizeArray(result.notes || result.tips || result.recommendations);
  if (notes.length) {
    sections.push(
      `${t("studioTools.brandVoiceTrainerTool.sections.notes")}:\n${notes
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const BrandVoiceTrainerTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();
  const [theme, setTheme] = useState(getStoredTheme());
  const [brandDescription, setBrandDescription] = useState("");
  const [audience, setAudience] = useState("");
  const [toneWords, setToneWords] = useState("");
  const [avoidWords, setAvoidWords] = useState("");
  const [sampleText, setSampleText] = useState("");
  const [goal, setGoal] = useState("");
  const [outputType, setOutputType] = useState("voice_profile");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(59,130,246,0.16)" : "rgba(59,130,246,0.10)",
        color: isDark ? "#93c5fd" : "#1d4ed8",
        border: `1px solid ${isDark ? "rgba(59,130,246,0.24)" : "rgba(59,130,246,0.18)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: isDark ? "#e2e8f0" : "#111827",
        color: isDark ? "#020617" : "#ffffff",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "#1e293b" : "#e5e7eb",
        color: isDark ? "#f8fafc" : "#111827",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.75,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
      outputBox: {
        borderRadius: 16,
        padding: 16,
        background: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResultToText(result, t), [result, t]);

  const voiceProfile =
    result?.voice_profile ||
    result?.brand_voice ||
    result?.profile ||
    result?.output ||
    result?.result ||
    "";

  const pillars = useMemo(
    () =>
      normalizeArray(
        result?.voice_pillars || result?.pillars || result?.core_traits || result?.traits
      ),
    [result]
  );

  const dos = useMemo(
    () => normalizeArray(result?.dos || result?.do_list || result?.best_practices),
    [result]
  );

  const donts = useMemo(
    () => normalizeArray(result?.donts || result?.do_not || result?.avoid),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setBrandDescription("");
    setAudience("");
    setToneWords("");
    setAvoidWords("");
    setSampleText("");
    setGoal("");
    setOutputType("voice_profile");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(t("studioTools.brandVoiceTrainerTool.messages.copyFailed"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleGenerate = async () => {
    if (!brandDescription.trim()) {
      setError(t("studioTools.brandVoiceTrainerTool.messages.brandDescriptionRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      topic: topicValue.trim(),
      platform: selectedPlatform,
      brand_description: brandDescription.trim(),
      target_audience: audience.trim(),
      tone_words: toneWords.trim(),
      avoid_words: avoidWords.trim(),
      sample_text: sampleText.trim(),
      goal: goal.trim(),
      output_type: outputType,
    };

    try {
      const data = await tryPost(
        [
          "/studio/brand-voice-trainer",
          "/studio/brand_voice_trainer",
          "/studio/brand-voice",
          "/studio/brand_voice",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.brand_voice ||
        data?.data ||
        data?.voice_profile ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.brandVoiceTrainerTool.messages.generateFailed");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.brandVoiceTrainerTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.brandVoiceTrainerTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.brandVoiceTrainerTool.description")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.brandVoiceTrainerTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.platform.label")}
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.brandVoiceTrainerTool.platformOptions.${option.labelKey}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.outputType.label")}
            </label>
            <select
              value={outputType}
              onChange={(e) => setOutputType(e.target.value)}
              style={styles.input}
            >
              {outputTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.brandVoiceTrainerTool.outputTypeOptions.${option.labelKey}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.outputType.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.targetAudience.label")}
            </label>
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.targetAudience.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.targetAudience.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.preferredToneWords.label")}
            </label>
            <input
              type="text"
              value={toneWords}
              onChange={(e) => setToneWords(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.preferredToneWords.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.preferredToneWords.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.avoidWords.label")}
            </label>
            <input
              type="text"
              value={avoidWords}
              onChange={(e) => setAvoidWords(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.avoidWords.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.avoidWords.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.brandVoiceTrainerTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.goal.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.brandDescription.label")}
            </label>
            <textarea
              value={brandDescription}
              onChange={(e) => setBrandDescription(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.brandDescription.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.brandDescription.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.brandVoiceTrainerTool.fields.sampleWriting.label")}
            </label>
            <textarea
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder={t("studioTools.brandVoiceTrainerTool.fields.sampleWriting.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.brandVoiceTrainerTool.fields.sampleWriting.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.brandVoiceTrainerTool.actions.generating")
              : t("studioTools.brandVoiceTrainerTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.brandVoiceTrainerTool.actions.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.brandVoiceTrainerTool.actions.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>{t("studioTools.brandVoiceTrainerTool.tip")}</span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard
            colors={{
              border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
              cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
              shadowSoft: isDark
                ? "0 10px 24px rgba(2,6,23,0.22)"
                : "0 6px 18px rgba(15,23,42,0.05)",
            }}
            index={0}
          >
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.brandVoiceTrainerTool.resultTitle")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.brandVoiceTrainerTool.actions.copied")
                    : t("studioTools.brandVoiceTrainerTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="brand_voice_trainer"
                  title={`${t("studioTools.brandVoiceTrainerTool.badge")}${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || t("studioTools.brandVoiceTrainerTool.badge")}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    brandDescription,
                    audience,
                    toneWords,
                    avoidWords,
                    sampleText,
                    goal,
                    outputType,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>
                {t(
                  `studioTools.brandVoiceTrainerTool.platformOptions.${
                    platformOptions.find((item) => item.value === selectedPlatform)?.labelKey || "general"
                  }`,
                  platformOptions.find((item) => item.value === selectedPlatform)?.fallback || "General"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.brandVoiceTrainerTool.outputTypeOptions.${
                    outputTypeOptions.find((item) => item.value === outputType)?.labelKey || "voiceProfile"
                  }`,
                  outputTypeOptions.find((item) => item.value === outputType)?.fallback || "Voice Profile"
                )}
              </span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {voiceProfile ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.brandVoiceProfile")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>{voiceProfile}</p>
                </div>
              </div>
            ) : null}

            {result?.summary || result?.overview || result?.analysis || result?.explanation ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.summary")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.summary || result?.overview || result?.analysis || result?.explanation}
                </p>
              </div>
            ) : null}

            {pillars.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.voicePillars")}
                </h4>
                <ul style={styles.list}>
                  {pillars.map((item, index) => (
                    <li key={`pillar-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {dos.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.dos")}
                </h4>
                <ul style={styles.list}>
                  {dos.map((item, index) => (
                    <li key={`do-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {donts.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.donts")}
                </h4>
                <ul style={styles.list}>
                  {donts.map((item, index) => (
                    <li key={`dont-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result?.sample_rewrite || result?.rewrite_example || result?.example_output ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.sampleRewrite")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>
                    {result?.sample_rewrite || result?.rewrite_example || result?.example_output}
                  </p>
                </div>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.brandVoiceTrainerTool.sections.rawResult")}
                </h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default BrandVoiceTrainerTool;